import { useEffect, useRef, useCallback, useState } from 'react'
import { getEcho, updateEchoAuth, disconnectEcho } from '../services/echo'

/**
 * Custom hook for managing WebSocket connections and channel subscriptions
 * @returns {Object} WebSocket connection state and methods
 */
export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState(null)
  const echoRef = useRef(null)
  const subscriptionsRef = useRef(new Map())

  // Initialize Echo connection
  useEffect(() => {
    const echo = getEcho()
    if (!echo) {
      setConnectionError('Echo not configured')
      return
    }

    echoRef.current = echo

    // Listen for connection state changes
    // Try to access connection through connector (works for both Reverb and Pusher-compatible connectors)
    const connector = echo.connector
    let connection = null
    let cleanupFunctions = []

    if (connector) {
      // Try pusher-compatible structure (in case @laravel/echo-react uses it internally)
      if (connector.pusher && connector.pusher.connection) {
        connection = connector.pusher.connection
      } else if (connector.connection) {
        // Try direct connection access (for Reverb)
        connection = connector.connection
      }
    }

    const handleConnected = () => {
      setIsConnected(true)
      setConnectionError(null)
    }

    const handleDisconnected = () => {
      setIsConnected(false)
    }

    const handleError = (error) => {
      setConnectionError(error?.message || 'Connection error')
      setIsConnected(false)
    }

    const handleStateChange = (states) => {
      if (states.current === 'connected') {
        setIsConnected(true)
        setConnectionError(null)
      } else if (states.current === 'disconnected' || states.current === 'failed') {
        setIsConnected(false)
      }
    }

    // Bind event listeners if connection is available
    if (connection && typeof connection.bind === 'function') {
      connection.bind('connected', handleConnected)
      cleanupFunctions.push(() => connection.unbind('connected', handleConnected))
      
      connection.bind('disconnected', handleDisconnected)
      cleanupFunctions.push(() => connection.unbind('disconnected', handleDisconnected))
      
      connection.bind('error', handleError)
      cleanupFunctions.push(() => connection.unbind('error', handleError))
      
      connection.bind('state_change', handleStateChange)
      cleanupFunctions.push(() => connection.unbind('state_change', handleStateChange))

      // Check initial connection state
      if (connection.state === 'connected') {
        setIsConnected(true)
      }
    } else {
      // If we can't access connection directly, assume connected when Echo is configured
      // This is a fallback for when @laravel/echo-react manages connections internally
      setIsConnected(true)
    }

    // Cleanup on unmount
    return () => {
      // Unbind all event listeners
      cleanupFunctions.forEach(cleanup => {
        try {
          cleanup()
        } catch (error) {
          console.warn('Error cleaning up connection listener:', error)
        }
      })

      // Unsubscribe from all channels
      subscriptionsRef.current.forEach((channel, channelName) => {
        try {
          echo.leave(channelName)
        } catch (error) {
          console.error(`Error leaving channel ${channelName}:`, error)
        }
      })
      subscriptionsRef.current.clear()
    }
  }, [])

  /**
   * Subscribe to a private channel
   * @param {string} channelName - Channel name (e.g., 'conversation.123')
   * @param {Function} callback - Callback function for events
   * @param {string} eventName - Event name to listen for (optional)
   * @returns {Object|null} Channel subscription or null
   */
  const subscribe = useCallback((channelName, callback, eventName = null) => {
    const echo = echoRef.current
    if (!echo) {
      console.error('Echo not initialized')
      return null
    }

    try {
      // Check if already subscribed
      if (subscriptionsRef.current.has(channelName)) {
        const existing = subscriptionsRef.current.get(channelName)
        // If event name is provided, add listener to existing channel
        if (eventName && callback) {
          existing.listen(eventName, callback)
        }
        return existing
      }

      // Subscribe to private channel
      const channel = echo.private(channelName)
      subscriptionsRef.current.set(channelName, channel)

      // If event name is provided, listen for it
      if (eventName && callback) {
        channel.listen(eventName, callback)
      } else if (callback) {
        // If no event name, assume callback handles all events
        channel.listen('.', callback)
      }

      return channel
    } catch (error) {
      console.error(`Error subscribing to channel ${channelName}:`, error)
      setConnectionError(error.message)
      return null
    }
  }, [])

  /**
   * Unsubscribe from a channel
   * @param {string} channelName - Channel name to unsubscribe from
   */
  const unsubscribe = useCallback((channelName) => {
    const echo = echoRef.current
    if (!echo) {
      return
    }

    try {
      if (subscriptionsRef.current.has(channelName)) {
        echo.leave(channelName)
        subscriptionsRef.current.delete(channelName)
      }
    } catch (error) {
      console.error(`Error unsubscribing from channel ${channelName}:`, error)
    }
  }, [])

  /**
   * Listen for a specific event on a channel
   * @param {string} channelName - Channel name
   * @param {string} eventName - Event name
   * @param {Function} callback - Callback function
   */
  const listen = useCallback((channelName, eventName, callback) => {
    const echo = echoRef.current
    if (!echo) {
      console.error('Echo not initialized')
      return
    }

    try {
      let channel = subscriptionsRef.current.get(channelName)
      
      if (!channel) {
        // Subscribe first if not already subscribed
        channel = subscribe(channelName, null)
      }

      if (channel) {
        channel.listen(eventName, callback)
      }
    } catch (error) {
      console.error(`Error listening to event ${eventName} on channel ${channelName}:`, error)
      setConnectionError(error.message)
    }
  }, [subscribe])

  /**
   * Stop listening to a specific event on a channel
   * @param {string} channelName - Channel name
   * @param {string} eventName - Event name
   */
  const stopListening = useCallback((channelName, eventName) => {
    const channel = subscriptionsRef.current.get(channelName)
    if (channel) {
      try {
        channel.stopListening(eventName)
      } catch (error) {
        console.error(`Error stopping listener for ${eventName} on channel ${channelName}:`, error)
      }
    }
  }, [])

  /**
   * Update authentication token
   * @param {string} token - New access token
   */
  const updateAuth = useCallback((token) => {
    updateEchoAuth(token)
    // Reconnect if disconnected
    if (!isConnected && echoRef.current) {
      const connector = echoRef.current.connector
      if (connector) {
        let connection = null
        if (connector.pusher && connector.pusher.connection) {
          connection = connector.pusher.connection
        } else if (connector.connection) {
          connection = connector.connection
        }
        
        if (connection && connection.state === 'disconnected' && typeof connection.connect === 'function') {
          connection.connect()
        }
      }
    }
  }, [isConnected])

  /**
   * Reconnect to WebSocket server
   */
  const reconnect = useCallback(() => {
    const echo = echoRef.current
    if (echo && echo.connector) {
      const connector = echo.connector
      let connection = null
      
      if (connector.pusher && connector.pusher.connection) {
        connection = connector.pusher.connection
      } else if (connector.connection) {
        connection = connector.connection
      }
      
      if (connection && connection.state !== 'connected' && typeof connection.connect === 'function') {
        connection.connect()
      }
    }
  }, [])

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    disconnectEcho()
    echoRef.current = null
    subscriptionsRef.current.clear()
    setIsConnected(false)
  }, [])

  return {
    isConnected,
    connectionError,
    subscribe,
    unsubscribe,
    listen,
    stopListening,
    updateAuth,
    reconnect,
    disconnect,
  }
}

export default useWebSocket

